import { formatUnits, parseUnits } from 'viem';
import { USDT_DECIMALS, BSC_EXPLORER } from './addresses';

export function formatUsdt(value, options = {}) {
  const { maximumFractionDigits = 2, minimumFractionDigits = 2 } = options;
  if (value === undefined || value === null) return '0.00';
  const raw = typeof value === 'bigint' ? formatUnits(value, USDT_DECIMALS) : String(value);
  const num = Number(raw);
  if (Number.isNaN(num)) return '0.00';
  return num.toLocaleString('en-US', {
    minimumFractionDigits,
    maximumFractionDigits,
  });
}

export function formatUsdtCompact(value) {
  if (value === undefined || value === null) return '0';
  const raw = typeof value === 'bigint' ? formatUnits(value, USDT_DECIMALS) : String(value);
  const num = Number(raw);
  if (Number.isNaN(num)) return '0';
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(2) + 'M';
  if (num >= 1_000) return (num / 1_000).toFixed(2) + 'K';
  return num.toFixed(2);
}

export function parseUsdt(value) {
  return parseUnits(String(value), USDT_DECIMALS);
}

export function bpsToPct(bps, digits = 2) {
  return (Number(bps) / 100).toFixed(digits);
}

export function shortAddr(addr, prefix = 6, suffix = 4) {
  if (!addr || typeof addr !== 'string') return '';
  if (addr.length <= prefix + suffix + 2) return addr;
  return `${addr.slice(0, prefix)}…${addr.slice(-suffix)}`;
}

export function formatDuration(seconds) {
  const s = Number(seconds);
  if (!Number.isFinite(s) || s <= 0) return '0s';
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export function bscTx(hash) {
  return `${BSC_EXPLORER}/tx/${hash}`;
}

export function bscAddr(addr) {
  return `${BSC_EXPLORER}/address/${addr}`;
}

// Длительность плана в секундах. В демо-планах есть поле `durationSeconds` (test-стратегии),
// в реальном контракте только `durationDays` — fallback на него.
export function planDurationSec(plan) {
  if (!plan) return 0;
  if (plan.durationSeconds) return Number(plan.durationSeconds);
  return Number(plan.durationDays) * 86400;
}

// Локализованный лейбл плана: "30d · 1.20%/day" или "10m · 1.00%/10min" для тестовой.
export function formatPlanLabel(plan, t) {
  if (!plan) return '';
  const daily = (Number(plan.dailyBps) / 100).toFixed(2);
  if (plan.isTest) {
    const seconds = Number(plan.durationSeconds ?? 600);
    const minutes = Math.round(seconds / 60);
    // dailyBps — годовая ставка в bps за сутки; пересчитаем в % за 10 мин:
    // periodPct = dailyBps * seconds / 86400 / 100
    const periodPct = ((Number(plan.dailyBps) * seconds) / 86400 / 100).toFixed(2);
    return t('dashboard.common.planTestLabel', { minutes, periodPct });
  }
  return t('dashboard.common.planDisplay', { days: plan.durationDays, daily });
}

// Чистая формула accrued из контракта:
//   accrued = amount * dailyBps * elapsed / (86400 * 10000)
// elapsed капает только внутри окна депозита (не после maturity).
export function computeAccrued({ amount, dailyBps, startedAt, lastClaimAt, durationDays, durationSec, now }) {
  const startedAtN = Number(startedAt);
  const lastClaimAtN = Number(lastClaimAt);
  const dur = durationSec !== undefined ? Number(durationSec) : Number(durationDays) * 86400;
  const endAt = startedAtN + dur;
  const capNow = Math.min(Number(now), endAt);
  const elapsed = Math.max(0, capNow - lastClaimAtN);
  if (elapsed === 0) return 0n;
  const amtBig = BigInt(amount);
  const bpsBig = BigInt(dailyBps);
  const elapsedBig = BigInt(elapsed);
  return (amtBig * bpsBig * elapsedBig) / (86400n * 10000n);
}

export function isMatured({ startedAt, durationDays, durationSec, now }) {
  const dur = durationSec !== undefined ? Number(durationSec) : Number(durationDays) * 86400;
  return Number(now) >= Number(startedAt) + dur;
}

export function matureAt({ startedAt, durationDays, durationSec }) {
  const dur = durationSec !== undefined ? Number(durationSec) : Number(durationDays) * 86400;
  return Number(startedAt) + dur;
}

export function progressPct({ startedAt, durationDays, durationSec, now }) {
  const dur = durationSec !== undefined ? Number(durationSec) : Number(durationDays) * 86400;
  if (dur <= 0) return 0;
  const pct = ((Number(now) - Number(startedAt)) / dur) * 100;
  return Math.max(0, Math.min(100, pct));
}

export function toCsv(rows, headers) {
  const esc = (v) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    return s.includes(',') || s.includes('"') || s.includes('\n')
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };
  const header = headers.map((h) => esc(h.label)).join(',');
  const body = rows.map((r) => headers.map((h) => esc(r[h.key])).join(',')).join('\n');
  return `${header}\n${body}`;
}
